import { Auth } from 'aws-amplify';
import { Formik } from 'formik';
import { toast } from 'react-hot-toast';

import http from 'src/api/endpoints';
import { Button, TextInput } from 'src/components';
import AuthContainer from 'src/Containers/AuthContainer';
import { signupValidationSchema } from './validation';
import OnboardingHeaderWrapper from 'src/components/Wrapper/OnboardingHeaderWrapper';
import { PARENT_URL } from 'src/config/parentURL';

interface SubmitValues {
  fullName: string;
  password: string;
  tempPassword?: string;
  userExists: boolean;
}

type SignUpData = {
  fullName: string;
  nextStep: any;
  userExists: boolean;
  tempPassword?: string;
  email: string;
};

type SignUpProps = SignUpData & {
  updateFields: (fields: Partial<SignUpData>) => void;
  nextStep: () => void;
};

const SignUpPage = ({
  nextStep,
  updateFields,
  userExists = true,
  email,
}: SignUpProps) => {
  const handleSubmit = async (values: SubmitValues) => {
    const { fullName, password, tempPassword } = values;
    if (fullName && password && tempPassword) {
      try {
        const createUser = await Auth.signIn(email, tempPassword);
        const { challengeName } = createUser || {};
        if (challengeName === 'NEW_PASSWORD_REQUIRED') {
          const user = await Auth.completeNewPassword(createUser, password);
          if (user) {
            const response = await http.updateProfile(
              {
                is_active: true,
              },
              PARENT_URL.BASE_SERVER
            );
            const { data } = response || {};
            if (data) {
              updateFields({ fullName });
              nextStep();
            }
          }
        }
      } catch (error) {
        toast.error('The username or current password is incorrect.');
      }
    }
  };

  const isDisabled = (values: any) => {
    const { fullName, password, tempPassword } = values || {};
    if (userExists) {
      return !fullName || !password || !tempPassword;
    }
    return !fullName || !password;
  };

  return (
    <AuthContainer>
      <OnboardingHeaderWrapper
        title="Enter your full name and set password"
        index={2}
        link="LOGIN"
      />
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          fullName: '',
          password: '',
          tempPassword: '',
          userExists: userExists ? true : false,
        }}
        validationSchema={signupValidationSchema}
        enableReinitialize
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="h-[390px] overflow-y-auto w-full pr-1">
              <TextInput
                label="Full Name"
                variant="normal"
                type="text"
                className="mb-6 "
                placeHolder="Enter Full Name"
                htmlLabelFor="fullName"
                validState={
                  touched.fullName && errors.fullName
                    ? 'inValid'
                    : touched.fullName
                    ? 'valid'
                    : undefined
                }
                value={values.fullName}
                helperMessage={errors.fullName}
                onChange={handleChange}
                onClear={() => setFieldValue('fullName', '')}
                onBlur={handleBlur}
              />
              {userExists ? (
                <TextInput
                  label="Temporary Password"
                  variant="normal"
                  type="text"
                  className="mb-6"
                  placeHolder="Enter Temporary Password"
                  htmlLabelFor="tempPassword"
                  validState={
                    touched.tempPassword && errors.tempPassword
                      ? 'inValid'
                      : touched.tempPassword
                      ? 'valid'
                      : undefined
                  }
                  value={values.tempPassword}
                  helperMessage={errors.tempPassword}
                  onChange={handleChange}
                  onClear={() => setFieldValue('tempPassword', '')}
                  onBlur={handleBlur}
                />
              ) : null}
              <TextInput
                label="Password"
                variant="normal"
                type="password"
                className="mb-4 peer placeholder:text-lg"
                placeHolder="Enter Password"
                htmlLabelFor="password"
                validState={
                  touched.password && errors.password
                    ? 'inValid'
                    : touched.password
                    ? 'valid'
                    : undefined
                }
                helperMessage={errors.password}
                value={values.password}
                onChange={handleChange}
                onClear={() => setFieldValue('password', '')}
                onBlur={handleBlur}
              />
              <p className="text-slate-500 text-[13px] font-medium font-SFProDisplay leading-[18px]">
                Password must be at least 6 characters long
              </p>
              <p className="text-blue-950 text-sm font-medium font-SFProDisplay leading-[18px] mt-6">
                By continuing you're agreeing to our{' '}
                <span className="text-lightBlue">
                  Terms of Service, Privacy{' '}
                </span>
                and <span className="text-lightBlue">Cookies Policy</span>.
              </p>
            </div>
            <Button
              variant="normal"
              size="sm"
              label="Continue"
              className="block w-full mx-0 mt-8 md:mt-12"
              type="submit"
              disabled={isDisabled(values) || Object.keys(errors).length > 0}
            />
          </form>
        )}
      </Formik>
    </AuthContainer>
  );
};
export default SignUpPage;
